import React, { Fragment, useEffect, useRef, useState } from 'react';
import { InputAdornment, Grid, Toolbar, Button, Checkbox, Dialog, DialogContent, DialogActions, Typography, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import ExtendedField from '../../components/text_field';
import Helpers, { ApiHelper } from '../../helpers';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import axios from 'axios';
import DownloadButton from '../../components/download_button';
import FileSaverBase from 'filesaver.js-npm';
import Auth from '../../auth';
import EventTable from '../../components/event_table';
import SearchBox from '../../components/searchbox';
import { Notifier } from '../documents/document_components';
import PapaBase from 'papaparse';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
// filesaver.js-npm ships no type declarations (matches reports.tsx's cast).
const FileSaver: any = FileSaverBase;
// papaparse ships no type declarations either (same treatment).
const Papa: any = PapaBase;

type ShipMethodMapping = {
    StandardCode: string;
    CarrierCode?: string;
    ServiceLevel?: string;
    Description?: string;
    CustomCode?: string | null;
    UsedByPartner?: boolean;
    [key: string]: any;
};

type Box = {
    Id?: number;
    BoxName?: string;
    Length?: number | string;
    Width?: number | string;
    Height?: number | string;
    [key: string]: any;
};

type ShipmentOptionsProps = {
    setTitle?: (title: string) => void;
    isRetailer?: boolean;
};

type ShipmentOptionsState = {
    showEdit: boolean;
    disableEdit: boolean;
    mapping: ShipMethodMapping | null;
    showError: boolean;
    showSuccess: boolean;
    showBoxConfirm: boolean;
    showBoxEdit: boolean;
    shipMethods: ShipMethodMapping[] | null;
    methodSearchText: string;
    partnerMethodSearchText: string;
    unusedHidden: boolean;
    validationWarnings: string[];
    error?: string;
    refresh?: boolean;
    refreshLog?: boolean;
    box?: Box | null;
    disableBoxConfirm?: boolean;
    disableBoxEdit?: boolean;
    boxRefresh?: boolean;
    partnerShipMethods?: any[];
    partnerMappings?: any[];
    refreshPartner?: boolean;
};

// Audit-log filter has no state/props dependencies; hoisted to module scope so
// its identity stays stable across renders (matches the class instance field,
// which was created once).
const eventFilter = {filters: [
      {field: "CustomerViewable", operator: 'eq', value: true},
      {filters: [
          {field: "EventTypeId", operator: 'eq', value: 69},
      ],
      logic: 'or'}
  ], logic: 'and'};

const ShipmentOptions = (props: ShipmentOptionsProps) => {
    // The class merged partial state slices and read several fields immediately
    // after setState, so keep the single-object shape it used.
    const [state, setState] = useState<ShipmentOptionsState>({showEdit: false, disableEdit: false, mapping: null, showError: false, showSuccess: false, showBoxConfirm: false,
        showBoxEdit: false, shipMethods: null, methodSearchText: '', partnerMethodSearchText: '', unusedHidden: true, validationWarnings: []});

    // Partial-merge updater preserving the class's setState({...}) semantics.
    const patchState = (partial: Partial<ShipmentOptionsState>) => setState(prev => ({...prev, ...partial}));

    // Unused in this component but retained to match the class instance field
    // (and to keep the ApiHelper import wired the same way).
    const apiHelper = useRef(new ApiHelper());
    void apiHelper;

    const mappingImport = useRef<HTMLInputElement | null>(null);

    const saveMapping = async (mapping: ShipMethodMapping) => {
        patchState({disableEdit: true});
        try{
            await axios.post(Config.api + "/odata/Company/Functions.UpdateGenericLookup", {lookupType: "ShipMethod", internalValue: mapping.StandardCode, externalValue: mapping.CustomCode});
        }catch (e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            patchState({showError: true, error: error});
        }
        let newState = await loadShipmentMethods();
        setState(prev => ({...prev, refresh: !prev.refresh, refreshLog: !prev.refreshLog, disableEdit: false, showEdit: false, mapping: null, ...newState}));
    }
    const downloadMappings = async () => {
        let csv = (await axios.get(Config.api + '/odata/Company/Functions.ExportShipmentMethods')).data.value;
        let blob = new Blob([csv], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, "shipment_method_mappings.csv");
    }
    const downloadPartnerMappings = async () => {
        let rows = [['Partner', 'Standard Code', 'Carrier Code', 'Service Level', 'Description', 'Your Code', 'Their Code']]
        .concat(state.partnerShipMethods!.map((r: any) => [r.CompanyName, r.StandardCode, r.CarrierCode, r.ServiceLevel, r.Description, r.YourCode, r.CustomCode]));
        let output = Papa.unparse(rows);
        let blob = new Blob([output], { type: "text/csv" });
        FileSaver.saveAs(blob, "partner_shipment_method_mappings.csv");
    }
    const uploadMappings = async (files?: FileList | null) => {
        if(!files || files.length === 0){
            return;
        }
        try {
            var mappings = await (new Promise(function(resolve, reject){
                const reader = new FileReader();
                reader.readAsText(files[0]);
                reader.onload = function(){
                  resolve(reader.result);
                };
                reader.onerror = function(error) { reject(error); };
            }));
            await axios.post(Config.api + "/odata/Company/Functions.ImportShipmentMethods", {csv: mappings});
            let newState = await loadShipmentMethods();
            setState(prev => ({...prev, refresh: !prev.refresh, refreshLog: !prev.refreshLog, showSuccess: true, ...newState}));
        } catch (e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            patchState({showError: true, error: error});
        }
        mappingImport.current!.value = '';
    }
    const deleteBox = async () => {
        patchState({disableBoxConfirm: true});
        await axios.delete(Config.api + `/odata/Company/Boxes(${state.box!.Id})`);
        setState(prev => ({...prev, showBoxConfirm: false, boxRefresh: !prev.boxRefresh}));
    }

    const updateBox = async (values: Box) => {
        patchState({disableBoxEdit: true});
        if(values.Id){
            await axios.put(Config.api + `/odata/Company/Boxes(${state.box!.Id})`, values);
        }else{
            await axios.post(Config.api + `/odata/Company/Boxes`, values);
        }
        setState(prev => ({...prev, showBoxEdit: false, boxRefresh: !prev.boxRefresh}));
    }

    const loadBox = async (id: number) => {
        patchState({box: null, showBoxEdit: true, disableBoxEdit: false});
        var box = (await axios.get(Config.api + `/odata/Company/Boxes(${id})`)).data;
        patchState({box});
    }
    const loadShipmentMethods = async () => {
        let methods = (await axios.get(Config.api + `/odata/Company/ShipMethodMappings`)).data.value;
        let usedByPartnerUnmapped = methods.filter((r: ShipMethodMapping) => r.UsedByPartner === true && !r.CustomCode).length;
        let validationWarnings: string[] = [];
        if(usedByPartnerUnmapped > 0 && !props.isRetailer){
            validationWarnings.push(`You are missing ${usedByPartnerUnmapped} mappings for codes used by your trading partners.`);
        }
        return {shipMethods: methods, validationWarnings};
    }
    const loadPartnerShipmentMethods = async () => {
        let methods = (await axios.get(Config.api + `/odata/Company/Functions.GetPartnerShipMethods`)).data.value;
        let mappedMethods = (await axios.get(Config.api + `/odata/Company/ShipMethodMappings?$filter=CustomCode ne null`)).data.value;
        return methods.map((r: any, i: number) => ({...r, id: i, YourCode: (mappedMethods.filter((x: any) => x.StandardCode === r.StandardCode)[0] || {}).CustomCode || null}));
    }
    const getFilteredShipMethods = async () => {
        let methods = state.shipMethods;
        if(!methods){
            let newState = await loadShipmentMethods();
            methods = newState.shipMethods;
            patchState(newState);
        }
        if(state.methodSearchText){
            var split = state.methodSearchText.toLowerCase().split(" ");
            var fields = ["StandardCode", "CarrierCode", "ServiceLevel", "Description", "CustomCode"];
            methods = methods!.filter((r: any) => split.every(s => fields.filter(f => r[f] && r[f].toLowerCase().indexOf(s) > -1).length > 0));
        }
        return methods;
    }
    const getPartnerShipMethods = async () => {
        let methods = state.partnerShipMethods;
        if(!methods){
            methods = await loadPartnerShipmentMethods();
            patchState({partnerShipMethods: methods});
        }
        return methods;
    }
    const getFilteredPartnerShipMethods = async () => {
        let methods = await getPartnerShipMethods();
        if(state.partnerMethodSearchText){
            var split = state.partnerMethodSearchText.toLowerCase().split(" ");
            var fields = ["StandardCode", "CarrierCode", "ServiceLevel", "Description", "CustomCode", "CompanyName", "YourCode"];
            methods = methods!.filter((r: any) => split.every(s => fields.filter(f => r[f] && r[f].toLowerCase().indexOf(s) > -1).length > 0));
        }
        return methods;
    }
    const editMapping = async (row: ShipMethodMapping) => {
        let partnerMappings = (await getPartnerShipMethods())!.filter((r: any) => r.StandardCode === row.StandardCode);
        patchState({showEdit: true, disableEdit: false, mapping: row, partnerMappings});
    }

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Shipment Options');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const baseMethodCols: any[] = [
      { id: 'StandardCode', filterable: true, sortable: true, label: 'Standard Code', width: '10em' },
      { id: 'CarrierCode', filterable: true, sortable: true, label: 'Carrier Code', width: '8em' },
      { id: 'ServiceLevel', filterable: true, sortable: true, label: 'Service Level', width: '8em' },
      { id: 'Description', filterable: true, sortable: true, label: 'Description' },
      { id: 'CustomCode', filterable: true, sortable: true, label: 'Custom Code', width: '12em' },
      { id: 'UsedByPartner', filterable: true, sortable: true, label: 'Used By Partner', width: '10em', type: 'bool', template: (value: any, row: any) => (<Checkbox checked={value}/>) }
    ];

    let canManageSettings = Auth.hasPermission('settings/manage');
    let boxConfig: any = {
        url: Config.api + '/odata/Company/Boxes',
        columns: [
          { id: "Id", hidden: true},
          { id: 'BoxName', filterable: true, label: 'Name', width: '12em' },
          { id: 'Length', label: 'Length' },
          { id: 'Width', label: 'Width' },
          { id: 'Height', label: 'Height' },
          { command: 'commands', stopPropagation: 'true', width: '25em', template: (value: any, row: Box) => <Fragment>
              <Button style={{marginRight: '1rem'}} variant='outlined' size='small' onClick={() => loadBox(row.Id!)}>Edit</Button>
              <Button variant='outlined' size='small' onClick={() => patchState({box: row, showBoxConfirm: true, disableBoxConfirm: false})}>Delete</Button>
          </Fragment>}
      ],
      order: 'asc',
      orderBy: 'BoxName',
      keyField: 'Id',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      title: 'Boxes',
    actions: <Button variant='outlined' size='small' onClick={() => patchState({box: {}, showBoxEdit: true, disableBoxEdit: false})}>New Box</Button>
    };
    let partnerConfig: any = {
        getData: getFilteredPartnerShipMethods,
        columns: [
            { id: 'CompanyName', filterable: true, sortable: true, label: 'Partner', width: '10em' },
            { id: 'StandardCode', filterable: true, sortable: true, label: 'Standard Code', width: '10em' },
            { id: 'CarrierCode', filterable: true, sortable: true, label: 'Carrier Code', width: '8em' },
            { id: 'ServiceLevel', filterable: true, sortable: true, label: 'Service Level', width: '8em' },
            { id: 'Description', filterable: true, sortable: true, label: 'Description' },
            { id: 'YourCode', filterable: true, sortable: true, label: 'Your Code',width: '20em'},
            { id: 'CustomCode', filterable: true, sortable: true, label: 'Their Code',width: '20em' }
      ],
      order: 'asc',
      orderBy: 'Description',
      keyField: 'id',
      pageSize: 20,
      pageSizes: [10, 20, 50],
      actions: <div style={{display: 'flex', flexDirection: 'row', flex: 1}}>
        <span style={{marginRight: '10px'}}>
                <SearchBox placeholder="Search mappings" onEnter={() => setState(prev => ({...prev, refreshPartner: !prev.refreshPartner}))} handleChange={(e: any) => patchState({partnerMethodSearchText: e.currentTarget.value})}/>
        </span>
        <DownloadButton style={{marginRight: '1rem'}} onClick={downloadPartnerMappings} variant='outlined' size='small'>Export CSV</DownloadButton>
        </div>,
      title: 'Partner Ship Method Mappings'
    };

    let methodConfig: any = {
      getData: getFilteredShipMethods,
      columns: [...baseMethodCols],
      order: 'asc',
      orderBy: 'Description',
      keyField: 'StandardCode',
      pageSize: 20,
      pageSizes: [10, 20, 50],
      actions: <div style={{display: 'flex', flexDirection: 'column', flex: 1}}>
            <div style={{display: 'flex', flexDirection: 'row', flex: 1}}>
                <span style={{marginRight: '10px'}}>
                    <SearchBox placeholder="Search mappings" onEnter={() => setState(prev => ({...prev, refresh: !prev.refresh}))} handleChange={(e: any) => patchState({methodSearchText: e.currentTarget.value})}/>
                </span>
                {canManageSettings && <Button variant='outlined' style={{marginRight: '1rem'}} onClick={() => mappingImport.current!.click()}size='small'>Import CSV</Button>}
                <DownloadButton style={{marginRight: '1rem'}} onClick={downloadMappings} variant='outlined' size='small'>Export CSV</DownloadButton>
                <Button size='small' variant='outlined' onClick={() => {setState(prev => ({...prev, unusedHidden: !prev.unusedHidden}))}}>{state.unusedHidden ? 'Show Unused Codes' : 'Hide Unused Codes'}</Button>
            </div>
            {state.validationWarnings.map((r, i) => <Notifier color="#FFB600" style={{marginTop: '5px', marginRight: '1em'}} key={i}>{r}</Notifier>)}
        </div>,
      title: 'Ship Method Mappings'
    }
    if(canManageSettings){
        methodConfig.columns.push({ command: 'commands', stopPropagation: 'true', width: '8em', template: (value: any, row: ShipMethodMapping) => (
            <Button size='small' variant='outlined' onClick={() => editMapping(row)}>Edit</Button>
        )});
    }
    if(state.unusedHidden){
        methodConfig.filter = {logic: 'or', filters: [{field: "UsedByPartner", operator: "eq", value: true, system: true}, {field: "CustomCode", operator: 'ne', value: null, system: true}]};
    }
    boxConfig.refresh = state.boxRefresh;
    partnerConfig.refresh = state.refreshPartner;
    methodConfig.refresh = state.refresh;
    return (
        <Grid container spacing={2}>
            {canManageSettings && <Grid size={12}>
                <EnhancedTable config={boxConfig} collapsible={true} collapsed={true}/>
            </Grid>}
            <Grid size={12}>
                <EnhancedTable config={methodConfig} collapsible={true}/>
            </Grid>
            <Grid size={12}>
                <EnhancedTable config={partnerConfig} collapsible={true} collapsed={true}/>
            </Grid>
            <Grid size={12}>
                <EventTable title="Audit Log" filter={eventFilter} refresh={state.refreshLog}/>
            </Grid>
            <input type="file" accept='.csv' id="mappingImport" ref={mappingImport} style={{display: "none"}} onChange={(e) => uploadMappings(e.target.files)}/>
            <Dialog
                maxWidth="xs"
                fullWidth
                open={state.showEdit}
                onClose={(event: any, reason: string) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableEdit) return;
                  }
                  patchState({showEdit: false, mapping: null});
                }}>
                {state.mapping ?
                <Form onSubmit={saveMapping}
                initialValues={state.mapping}
                render={({ handleSubmit, pristine, invalid, values }: any) => (
                  <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                  <Toolbar className='lbtoolbar'>{`Edit Mapping for ${state.mapping!.StandardCode}`}</Toolbar>
                <DialogContent style={{paddingBottom: '0'}}>
                  <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                    <Field label='Custom Code' component={ExtendedField} name='CustomCode'/>
                    {state.partnerMappings && state.partnerMappings.length
                    ? <Table style={{marginTop: '0.5em', overflow: 'auto'}}>
                        <TableHead style={{height: 'auto', paddingTop: '0', paddingBottom: '0'}}>
                            <TableRow style={{height: 'auto', paddingTop: '0', paddingBottom: '0'}}>
                                <TableCell>Partner</TableCell>
                                <TableCell>Their Code</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.partnerMappings.map((r: any) => <TableRow key={r.CoId} style={{height: 'auto', paddingTop: '0', paddingBottom: '0'}}>
                                <TableCell style={{paddingRight: "0"}}>{r.CompanyName}</TableCell>
                                <TableCell style={{paddingRight: "0"}}>{r.CustomCode}</TableCell>
                                </TableRow>)}
                        </TableBody>
                    </Table>
                    : <Typography style={{marginTop: '0.5em'}}>This code is not used by any of your trading partners.</Typography>}
                  </div>
                </DialogContent>
                                    <DialogActions>
                                        <Button type='submit' variant='text' disabled={pristine || invalid || state.disableEdit} color="primary">
                                            Save
                                        </Button>
                                    </DialogActions>
                </form>
            )}/> : ''}
              </Dialog>
              <Dialog open={state.showError} onClose={() => {patchState({showError: false})}}>
                  <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                  <DialogContent><Typography>{state.error}</Typography></DialogContent>
              </Dialog>
              <Dialog open={state.showSuccess} onClose={() => {patchState({showSuccess: false})}}>
                  <Toolbar className='lbtoolbar'>{'Success'}</Toolbar>
                  <DialogContent><Typography>Ship method mappings were successfully imported.</Typography></DialogContent>
              </Dialog>
              <Dialog
                  maxWidth="sm"
                  open={state.showBoxConfirm}
                  onClose={(event: any, reason: string) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                      if (state.disableBoxConfirm) return;
                    }
                    patchState({showBoxConfirm: false});
                  }}>
                  <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                  <DialogContent>
                    <Typography>Are you sure you want to delete this box?</Typography>
                  </DialogContent>
                                        <DialogActions>
                                            <Button variant='outlined' onClick={() => patchState({showBoxConfirm: false})} disabled={state.disableBoxConfirm} color="primary">
                                                No
                                            </Button>
                                            <Button variant='outlined' onClick={deleteBox} disabled={state.disableBoxConfirm} color="primary">
                                                Yes
                                            </Button>
                                        </DialogActions>
                </Dialog>
                  <Dialog
                      maxWidth="sm"
                      open={state.showBoxEdit}
                      onClose={(event: any, reason: string) => {
                        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                          if (state.disableBoxEdit) return;
                        }
                        patchState({showBoxEdit: false});
                      }}
                      fullWidth={true}>
                      {state.box ?
                          <Form onSubmit={updateBox}
                          initialValues={state.box}
                          render={({ handleSubmit, pristine, invalid, values }: any) => (
                            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                            <Toolbar className='lbtoolbar'>Edit Box</Toolbar>
                          <DialogContent>
                          <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                            <Field validate={(value: any) => value ? undefined : 'Required'} label='Box Name' component={ExtendedField} name='BoxName'/>
                            <Field validate={(value: any) => value ? undefined : 'Required'} label='Length' component={ExtendedField} name='Length' type='number' inputProps={{min: 0}} endAdornment={<InputAdornment position="end">in</InputAdornment>}/>
                            <Field validate={(value: any) => value ? undefined : 'Required'} label='Width' component={ExtendedField} name='Width' type='number' inputProps={{min: 0}} endAdornment={<InputAdornment position="end">in</InputAdornment>}/>
                            <Field validate={(value: any) => value ? undefined : 'Required'} label='Height' component={ExtendedField} name='Height' type='number' inputProps={{min: 0}} endAdornment={<InputAdornment position="end">in</InputAdornment>}/>
                            </div>
                          </DialogContent>
                                                        <DialogActions>
                                                            <Button type='submit' variant='text' disabled={pristine || invalid || state.disableBoxEdit} color="primary">
                                                                Save
                                                            </Button>
                                                        </DialogActions>
                          </form>
                          )}/>
                           :  <DialogContent><LinearProgress style={{height: '2em'}} variant="indeterminate"/></DialogContent> }
                    </Dialog>
        </Grid>
    );
}

export default ShipmentOptions;
